import React, { useState, useEffect, useCallback } from "react";
import { ChevronRight, ChevronLeft, Settings, AlertCircle } from "lucide-react";
import { useImportStore } from "../../store/useImportStore";
import { cn } from "../../utils/helpers";
import type {
  ColumnMapping as ColumnMappingType,
  ImportConfig,
} from "../../types/import";

const TARGET_FIELDS = [
  { value: "ignore", label: "Ignore Column", required: false },
  { value: "bike_id", label: "Bike/Device ID", required: true },
  { value: "latitude", label: "Latitude", required: true },
  { value: "longitude", label: "Longitude", required: true },
  { value: "timestamp", label: "Timestamp", required: true },
  { value: "speed", label: "Speed (km/h)", required: false },
  { value: "battery_level", label: "Battery Level (%)", required: false },
];

const DATA_TYPES = [
  { value: "string", label: "Text" },
  { value: "number", label: "Number" },
  { value: "datetime", label: "Date/Time" },
  { value: "boolean", label: "True/False" },
];

export const ColumnMapping: React.FC = () => {
  const {
    preview,
    currentFile,
    setImportConfig,
    setCurrentStep,
    startImport,
    loading,
  } = useImportStore();

  const [mappings, setMappings] = useState<ColumnMappingType[]>([]);
  const [formatSettings, setFormatSettings] = useState<
    Omit<ImportConfig, "column_mappings">
  >({
    delimiter: ",",
    has_header: true,
    skip_rows: 0,
    encoding: "utf8",
    timezone: "UTC",
  });
  const [errors, setErrors] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (preview?.suggested_mappings) {
      setMappings(preview.suggested_mappings);
    }
    if (preview?.detected_format) {
      setFormatSettings((prev) => ({
        ...prev,
        ...preview.detected_format,
      }));
    }
  }, [preview]);

  const updateMapping = (
    index: number,
    field: keyof ColumnMappingType,
    value: string | boolean
  ) => {
    const newMappings = [...mappings];
    newMappings[index] = { ...newMappings[index], [field]: value };

    // Auto-update data type based on target field
    if (field === "target_field") {
      switch (value) {
        case "bike_id":
          newMappings[index].data_type = "string";
          break;
        case "latitude":
        case "longitude":
        case "speed":
        case "battery_level":
          newMappings[index].data_type = "number";
          break;
        case "timestamp":
          newMappings[index].data_type = "datetime";
          break;
        default:
          newMappings[index].data_type = "string";
      }
    }

    setMappings(newMappings);
  };

  const validateMappings = (): string[] => {
    const errors: string[] = [];
    const requiredFields = TARGET_FIELDS.filter((f) => f.required).map(
      (f) => f.value
    );
    const mappedFields = mappings
      .map((m) => m.target_field)
      .filter((f) => f !== "ignore");

    // Check for required fields
    for (const requiredField of requiredFields) {
      if (!mappedFields.includes(requiredField)) {
        const fieldLabel = TARGET_FIELDS.find(
          (f) => f.value === requiredField
        )?.label;
        errors.push(
          `Required field "${fieldLabel}" is not mapped to any column`
        );
      }
    }

    // Check for duplicate mappings
    const nonIgnoredMappings = mappings.filter(
      (m) => m.target_field !== "ignore"
    );
    const fieldCounts = nonIgnoredMappings.reduce((acc, mapping) => {
      acc[mapping.target_field] = (acc[mapping.target_field] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    for (const [field, count] of Object.entries(fieldCounts)) {
      if (count > 1) {
        const fieldLabel = TARGET_FIELDS.find((f) => f.value === field)?.label;
        errors.push(`Field "${fieldLabel}" is mapped to multiple columns`);
      }
    }

    return errors;
  };

  const handlePrevious = () => {
    setCurrentStep("preview");
  };

  const handleStartImport = useCallback(async () => {
    const validationErrors = validateMappings();

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    const config: ImportConfig = {
      ...formatSettings,
      column_mappings: mappings,
    };

    setImportConfig(config);
    setErrors([]);
    setIsSubmitting(true);

    try {
      if (!currentFile) {
        throw new Error("No file selected");
      }

      await startImport(currentFile, config);
      setCurrentStep("progress");
    } catch (error) {
      console.error("Error starting import:", error);
      setErrors([
        error instanceof Error ? error.message : "Failed to start import",
      ]);
    } finally {
      setIsSubmitting(false);
    }
  }, [formatSettings, mappings, currentFile]);

  if (!preview || !currentFile) {
    return (
      <div className="p-8">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            No Preview Data
          </h2>
          <p className="text-gray-600 mb-6">
            Unable to load the file preview. Please go back and select a file.
          </p>
          <button
            onClick={() => setCurrentStep("upload")}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Back to Upload
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Configure Column Mapping
        </h2>
        <p className="text-gray-600">
          Map your CSV columns to the appropriate database fields and configure
          import settings.
        </p>
      </div>

      {/* Error Messages */}
      {errors.length > 0 && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-red-800 mb-2">
                Please fix the following issues:
              </h3>
              <ul className="text-sm text-red-700 space-y-1">
                {errors.map((error, index) => (
                  <li key={index}>• {error}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Format Settings */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Settings className="w-5 h-5 mr-2" />
          File Format Settings
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Delimiter
            </label>
            <select
              value={formatSettings.delimiter}
              onChange={(e) =>
                setFormatSettings((prev) => ({
                  ...prev,
                  delimiter: e.target.value as "," | ";" | "\t" | "|",
                }))
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value=",">Comma (,)</option>
              <option value=";">Semicolon (;)</option>
              <option value="\t">Tab</option>
              <option value="|">Pipe (|)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Encoding
            </label>
            <select
              value={formatSettings.encoding}
              onChange={(e) =>
                setFormatSettings((prev) => ({
                  ...prev,
                  encoding: e.target.value as "utf8" | "latin1" | "ascii",
                }))
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="utf8">UTF-8</option>
              <option value="latin1">Latin-1</option>
              <option value="ascii">ASCII</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Skip Rows
            </label>
            <input
              type="number"
              min="0"
              max="10"
              value={formatSettings.skip_rows}
              onChange={(e) =>
                setFormatSettings((prev) => ({
                  ...prev,
                  skip_rows: parseInt(e.target.value) || 0,
                }))
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Timezone
            </label>
            <select
              value={formatSettings.timezone}
              onChange={(e) =>
                setFormatSettings((prev) => ({
                  ...prev,
                  timezone: e.target.value,
                }))
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="UTC">UTC</option>
              <option value="America/New_York">Eastern Time</option>
              <option value="America/Chicago">Central Time</option>
              <option value="America/Denver">Mountain Time</option>
              <option value="America/Los_Angeles">Pacific Time</option>
              <option value="Europe/London">London</option>
              <option value="Europe/Paris">Paris</option>
              <option value="Asia/Tokyo">Tokyo</option>
            </select>
          </div>
        </div>

        <div className="mt-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formatSettings.has_header}
              onChange={(e) =>
                setFormatSettings((prev) => ({
                  ...prev,
                  has_header: e.target.checked,
                }))
              }
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="ml-2 text-sm text-gray-700">
              File has header row
            </span>
          </label>
        </div>
      </div>

      {/* Column Mappings */}
      <div className="bg-white rounded-lg border border-gray-200 mb-6">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Column Mappings
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Map each CSV column to the appropriate database field. Required
            fields must be mapped.
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">
                  CSV Column
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">
                  Sample Data
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">
                  Map to Field
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">
                  Data Type
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">
                  Required
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {mappings.map((mapping, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">
                      {mapping.csv_column}
                    </div>
                  </td>

                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-600 font-mono max-w-xs truncate">
                      {preview.sample_rows[0]?.[index] || "N/A"}
                    </div>
                  </td>

                  <td className="px-6 py-4">
                    <select
                      value={mapping.target_field}
                      onChange={(e) =>
                        updateMapping(index, "target_field", e.target.value)
                      }
                      className={cn(
                        "w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500",
                        TARGET_FIELDS.find(
                          (f) => f.value === mapping.target_field
                        )?.required && mapping.target_field === "ignore"
                          ? "border-red-300 bg-red-50"
                          : "border-gray-300"
                      )}
                    >
                      {TARGET_FIELDS.map((field) => (
                        <option key={field.value} value={field.value}>
                          {field.label} {field.required ? "*" : ""}
                        </option>
                      ))}
                    </select>
                  </td>

                  <td className="px-6 py-4">
                    <select
                      value={mapping.data_type}
                      onChange={(e) =>
                        updateMapping(index, "data_type", e.target.value)
                      }
                      disabled={mapping.target_field === "ignore"}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      {DATA_TYPES.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </td>

                  <td className="px-6 py-4">
                    <span
                      className={cn(
                        "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium",
                        TARGET_FIELDS.find(
                          (f) => f.value === mapping.target_field
                        )?.required
                          ? "bg-red-100 text-red-800"
                          : "bg-gray-100 text-gray-800"
                      )}
                    >
                      {TARGET_FIELDS.find(
                        (f) => f.value === mapping.target_field
                      )?.required
                        ? "Required"
                        : "Optional"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Required Fields Summary */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h4 className="text-sm font-medium text-blue-800 mb-2">
          Required Fields
        </h4>
        <p className="text-sm text-blue-700 mb-2">
          The following fields must be mapped for successful import:
        </p>
        <div className="flex flex-wrap gap-2">
          {TARGET_FIELDS.filter((f) => f.required).map((field) => {
            const isMapped = mappings.some(
              (m) => m.target_field === field.value
            );
            return (
              <span
                key={field.value}
                className={cn(
                  "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium",
                  isMapped
                    ? "bg-green-100 text-green-800"
                    : "bg-red-100 text-red-800"
                )}
              >
                {field.label} {isMapped ? "✓" : "✗"}
              </span>
            );
          })}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between">
        <button
          onClick={handlePrevious}
          disabled={loading}
          className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronLeft className="w-4 h-4" />
          <span>Previous</span>
        </button>

        <button
          onClick={handleStartImport}
          disabled={loading || isSubmitting}
          className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <div className="w-5 h-5 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>Starting Import...</span>
            </>
          ) : (
            <>
              <span>Start Import</span>
              <ChevronRight className="w-4 h-4" />
            </>
          )}
        </button>
      </div>
    </div>
  );
};
